import { FormControl, FormHelperText, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React from 'react';
import { createSelector } from 'reselect';
import lodash from 'lodash';
import { IPumpingStationVoluteCasingMake } from '../../../../@types/model/masterData/pumpingStation/voluteCasingMake';
import { connect } from 'react-redux';
import { IRootState } from '../../../../@types/redux';
import MasterDataPumpingStationVoluteCasingMakeFunctions from '../../../../store/masterData/pumpingStation/voluteCasingMake/functions';

interface IPumpingStationVoluteCasingMakeSelectorProps {
    makeValue : string;
    modelValue : string;
    disabled ?: boolean;
    required ?: boolean;
    className ?: string;
    onMakeChange : (value : string) => void;
    onModelChange : (value : string) => void;

    isLoading : boolean;
    voluteCasingMakes : Array<IPumpingStationVoluteCasingMake>;
}

interface IPumpingStationVoluteCasingMakeSelectorState { }

class PumpingStationVoluteCasingMakeSelectorComponent extends React.Component<IPumpingStationVoluteCasingMakeSelectorProps, IPumpingStationVoluteCasingMakeSelectorState> {
    constructor(props : IPumpingStationVoluteCasingMakeSelectorProps) {
        super(props);

        this.state = {};
    }

    public componentDidMount() {
        MasterDataPumpingStationVoluteCasingMakeFunctions.load();
    }

    private readonly onMakeChange = (event : React.ChangeEvent<unknown>, value : {
        label : string;
        value : string;
    } | null | undefined) => {
        this.props.onMakeChange(value?.value ?? '');
    };

    private readonly onModelChange = (event : React.ChangeEvent<unknown>, value : {
        label : string;
        value : string;
    } | null | undefined) => {
        this.props.onModelChange(value?.value ?? '');
    };

    private readonly getData = (state : IPumpingStationVoluteCasingMakeSelectorState, props : IPumpingStationVoluteCasingMakeSelectorProps) => props.voluteCasingMakes;
    private readonly getMakeValue = (state : IPumpingStationVoluteCasingMakeSelectorState, props : IPumpingStationVoluteCasingMakeSelectorProps) => props.makeValue;
    private readonly getModelValue = (state : IPumpingStationVoluteCasingMakeSelectorState, props : IPumpingStationVoluteCasingMakeSelectorProps) => props.modelValue;
    private readonly getRequired = (state : IPumpingStationVoluteCasingMakeSelectorState, props : IPumpingStationVoluteCasingMakeSelectorProps) => props.required;

    private readonly getVoluteCasingMakes = createSelector([
        this.getData,
    ], (pumpModels) => pumpModels.filter(x => x.isActive).map(x => x.name).sort());

    private readonly getPumpModels = createSelector([
        this.getMakeValue,
        this.getData,
    ], (make, pumpModels) => {
        const voluteCasingMake = pumpModels.find(x => x.name === make);

        return voluteCasingMake?.types ?? [];
    });

    private readonly getVoluteCasingMakesForDropdown = createSelector([ 
        this.getRequired, this.getVoluteCasingMakes,
    ], (required, voluteCasingMakes) => {
        
        const dropdownData = lodash.map(voluteCasingMakes, (element) => ({ label: element, value: element }));
        
        if (!required) {
            dropdownData.unshift({
                label: 'ALL',
                value: '',
            });
        }

        return dropdownData;
    });

    private readonly getPumpModelsForDropdown = createSelector([ 
        this.getRequired, this.getPumpModels,
    ], (required, pumpModels) => {
        
        const dropdownData = lodash.map(pumpModels, (element) => ({ label: element, value: element }));
        
        if (!required) {
            dropdownData.unshift({
                label: 'ALL',
                value: '',
            });
        }

        return dropdownData;
    });

    private readonly getSelectedVoluteCasingMake = createSelector([ 
        this.getMakeValue, this.getRequired, this.getVoluteCasingMakes,
    ], (value, required, voluteCasingMakes) => {
        if (value) {
            const voluteCasingMake = voluteCasingMakes.find(element => element === value);

            if (voluteCasingMake) {
                return { label: voluteCasingMake, value: voluteCasingMake };
            } else {
                return { label: value, value };
            }
        }

        if (!value && !required) {
            return {
                label: 'ALL',
                value: '',
            };
        }

        return null;
    });

    private readonly getSelectedPumpModel = createSelector([ 
        this.getModelValue, this.getRequired, this.getPumpModels,
    ], (value, required, pumpModels) => {
        if (value) {
            const pumpModel = pumpModels.find(element => element === value);

            if (pumpModel) {
                return { label: pumpModel, value: pumpModel };
            } else {
                return { label: value, value };
            }
        }

        if (!value && !required) {
            return {
                label: 'ALL',
                value: '',
            };
        }

        return null;
    });

    public readonly render = () => {
        const { required, className, disabled, isLoading } = this.props;

        const dropdownDataForMakes = this.getVoluteCasingMakesForDropdown(this.state, this.props);
        const dropdownDataForModels = this.getPumpModelsForDropdown(this.state, this.props);

        const makeValue = this.getSelectedVoluteCasingMake(this.state, this.props);
        const modelValue = this.getSelectedPumpModel(this.state, this.props);

        return (
            <>
                <FormControl margin={'normal'} fullWidth className={className} disabled={disabled} error={required && !makeValue}>
                    <Autocomplete 
                        disabled={isLoading}
                        id={'voluteCasingMake'}
                        options={dropdownDataForMakes}
                        value={makeValue}
                        getOptionSelected={(option, val) => option.value === val.value}
                        getOptionLabel={option => option.label}
                        onChange={this.onMakeChange}
                        disableClearable={required}
                        openOnFocus
                        renderInput={params => <TextField error={required && !makeValue} {...params} fullWidth label={'Volute Casing Make'} />} 
                    />   
                    {
                        required && !makeValue &&
                        <FormHelperText error>Required</FormHelperText>
                    }
                </FormControl>
                <FormControl margin={'normal'} fullWidth className={className} disabled={disabled} error={required && !modelValue}>
                    <Autocomplete 
                        disabled={isLoading}
                        id={'pumpModel'}
                        options={dropdownDataForModels}
                        value={modelValue}
                        getOptionSelected={(option, val) => option.value === val.value}
                        getOptionLabel={option => option.label}
                        onChange={this.onModelChange}
                        disableClearable={required}
                        openOnFocus
                        renderInput={params => <TextField error={required && !modelValue} {...params} fullWidth label={'Volute Casing Model'} />} 
                    />
                    {
                        required && !modelValue &&
                        <FormHelperText error>Required</FormHelperText>
                    }
                </FormControl>
            </>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        isLoading: state.masterData.pumpingStation.voluteCasingMake.isLoading,
        voluteCasingMakes: state.masterData.pumpingStation.voluteCasingMake.voluteCasingMakes,
    };
};

const PumpingStationVoluteCasingMakeSelector = connect(
    mapStateToProps,
)(PumpingStationVoluteCasingMakeSelectorComponent);

export default PumpingStationVoluteCasingMakeSelector;